import React, { useRef, useState } from 'react';
import Chip, { type ChipProps } from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import styles from 'src/pages/landlord/tours/components/calendar/SelectTourStatus.module.css';

/**
 * The three attendance states a tour can be in, keyed on the nullable
 * `is_attended` boolean: null (not yet recorded), true (attended), false (missed).
 */
const STATUS_OPTIONS: {
    value: boolean | null;
    label: string;
    color: string;
    backgroundColor: string;
}[] = [
    { value: null, label: 'Scheduled', color: '#5f6368', backgroundColor: '#e8eaed' },
    { value: true, label: 'Attended', color: '#188038', backgroundColor: '#e6f4ea' },
    { value: false, label: 'Missed', color: '#c5221f', backgroundColor: '#fce8e6' },
];

const optionFor = (isAttended: boolean | null) =>
    STATUS_OPTIONS.find((o) => o.value === isAttended) ?? STATUS_OPTIONS[0];

type StatusChipProps = ChipProps & {
    value: boolean | null;
};

export function TourStatusChip({ value, ...props }: StatusChipProps) {
    const option = optionFor(value);

    return (
        <Chip
            label={option.label}
            variant="filled"
            clickable
            {...props}
            classes={{
                root: styles.chip,
                label: styles.chipLabel,
            }}
            style={{ color: option.color, backgroundColor: option.backgroundColor }}
            data-testid="tour-status-chip"
        />
    );
}

type Props = {
    value: boolean | null;
    onChange: (value: boolean | null) => void;
    /** When false, the chip renders as a static badge with no dropdown. */
    clickable?: boolean;
};

/**
 * Tour attendance selector, modeled on SelectLeadStage: a colored chip that opens
 * a dropdown of the three attendance states (scheduled / attended / missed).
 */
export default function SelectTourStatus({ value, onChange, clickable = true }: Props) {
    const ref = useRef<HTMLDivElement>(null);
    const [isOpened, setOpened] = useState(false);

    const handleMenuItemClick = (newValue: boolean | null) => {
        setOpened(false);
        onChange(newValue);
    };

    return (
        <div ref={ref}>
            <TourStatusChip
                value={value}
                onClick={clickable ? () => setOpened(true) : undefined}
                clickable={clickable}
            />

            {isOpened && (
                <Menu anchorEl={ref.current} open={true} onClose={() => setOpened(false)}>
                    {STATUS_OPTIONS.map((option) => (
                        <MenuItem
                            key={option.label}
                            onClick={() => handleMenuItemClick(option.value)}
                            className={option.value === value ? styles.selectedItem : undefined}
                        >
                            <TourStatusChip value={option.value} clickable={false} />
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </div>
    );
}
